import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  Activity,
  ActivityConfiguration,
  OptimizeDeferralRequest,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import NaActivityEditDialog from '@totem/components/unauthenticated/ticketing/details/naActivityEditModal';
import { isNotNull } from '@totem/utilities/common';
import { UNAUTHENTICATED_TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  token: string;
  children?: ReactNode;
};

const NATicketContainer = ({ token, children }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ticketData, setTicketData] = useState<TicketQueryResult>(null);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [action, setAction] = useState<string>('');
  const [actionExtras, setActionExtras] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [eligibleActivities, setEligibleActivities] = useState<
    ActivityConfiguration[]
  >([]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);

      fetch(`${UNAUTHENTICATED_TICKET_ENDPOINT}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
      })
        .then((res) => res.json())
        .then((result: TicketQueryResult) => {
          setTicketData(result);
        })
        .then(() => setIsLoading(false));
    }
  }, [token, refresh]);

  useEffect(() => {
    if (
      isNotNull(ticketData) &&
      isNotNull(ticketData.ticket) &&
      ticketData.ticket.id !== ''
    ) {
      fetch(`${UNAUTHENTICATED_TICKET_ENDPOINT}/activities/eligible`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
      })
        .then((res) => res.json())
        .then((result: ActivityConfiguration[]) => {
          setEligibleActivities(result);
        });
    }
  }, [ticketData]);

  const handleTicketUpdate = (updatedData: TicketQueryResult) => {
    setTicketData(updatedData);
  };

  const handleAction = (newAction: string, extras: string) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);

        setAction('');
        break;
      case 'close-activity-dialog':
        setAction('');
        break;
      default:
        setActionExtras(extras);

        setAction(newAction);
    }
  };

  const sendActivity = useCallback(
    async (request: Activity) => {
      if (isSending) {
        return;
      }

      fetch(`${UNAUTHENTICATED_TICKET_ENDPOINT}/activities`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(request),
      })
        .then((res) => res.json())
        .then((result) => {
          handleTicketUpdate(result);

          handleAction('refresh', '');
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );

  const handleCreateActivity = (request: Activity) => {
    sendActivity(request);
  };

  const sendOptimizeDeferral = useCallback(
    async (paramTicketId: string, payload: OptimizeDeferralRequest) => {
      if (!isSending) {
        setIsSending(true);

        fetch(`${UNAUTHENTICATED_TICKET_ENDPOINT}/optimize/defer`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            handleAction('refresh', '');
          }
        });
      }
    },

    [isSending, handleAction],
  );

  const handleDeferral = (deferral: OptimizeDeferralRequest) => {
    sendOptimizeDeferral(ticketData.ticket.id, deferral);
  };

  return (
    <TicketContext.Provider
      value={{
        ticket: ticketData,
        activityConfiguration: isNotNull(eligibleActivities)
          ? eligibleActivities
          : [],
        loading: isLoading,
        action,
        actionExtras,
        onAction: handleAction,
        onUpdateTicket: handleTicketUpdate,
        onCreateActivity: handleCreateActivity,
        onOptimizeDeferral: handleDeferral,
      }}
    >
      {children}
      {action === 'show-activity-create' && <NaActivityEditDialog />}
    </TicketContext.Provider>
  );
};

export default NATicketContainer;
